import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import { useAuth } from 'firebaseHooks/AuthContext';
import useGlobal from 'global-state/store';
import useFirestoreCollectionData from 'firebaseHooks/useFirestoreCollectionData';
import {
  getDoc, doc,
} from 'firebase/firestore';
import Members from './Members';
import MyMembership from '../MyMembership';
import OrgApps from './OrgApps';

export default function Organization({ organization }) {
  const db = useFirestore();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [globalState, globalActions] = useGlobal();
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);

  // Real-time collection for organization members
  const membersCollectionPath = `organizations/${organization.id}/members`;
  const { data: membersData, loadingState: membersStatus } = useFirestoreCollectionData(membersCollectionPath);

  // Real-time collection for pending invitations
  const pendingCollectionPath = 'pendingOrganizationInvitations';
  const { data: pendingData, loadingState: pendingStatus } = useFirestoreCollectionData(pendingCollectionPath);

  // Process members data when it changes
  useEffect(() => {
    if (membersData && membersStatus === 'success') {
      const processMembers = async () => {
        const processedMembers = await Promise.all(
          membersData.map(async (memberDoc) => {
            const memberData = { ...memberDoc };

            // Get user email
            try {
              const userDoc = await getDoc(doc(db, 'users', memberDoc.id));
              if (userDoc.exists()) {
                memberData.email = userDoc.data().email;
              } else {
                memberData.email = t('organizations.user_removed');
              }
            } catch (error) {
              memberData.email = t('organizations.user_removed');
            }

            // Get membership state
            try {
              const membershipDoc = await getDoc(
                doc(db, 'organizations', organization.id, 'members', memberDoc.id, 'data', 'membership'),
              );
              if (membershipDoc.exists()) {
                memberData.membershipState = membershipDoc.data().membershipState;
              }
            } catch (error) {
              memberData.membershipState = 'unknown';
            }

            return memberData;
          }),
        );
        setMembers(processedMembers);
      };

      processMembers();
    }
  }, [membersData, membersStatus, db, organization.id, t]);

  // Process pending members data when it changes
  useEffect(() => {
    if (pendingData && pendingStatus === 'success') {
      // Filter pending members for this organization
      const processedPending = pendingData
        .filter((pendingDoc) => pendingDoc.organizationId === organization.id
          && pendingDoc.consumed === false)
        .sort((a, b) => a.email.localeCompare(b.email));
      setPendingMembers(processedPending);
    }
  }, [pendingData, pendingStatus, organization.id]);

  const isLoading = membersStatus === 'loading' || pendingStatus === 'loading';

  // Copy to clipboard functionality
  const handleCopyPath = async () => {
    const firestorePath = `/databases/(default)/documents/organizations/${organization.id}`;
    try {
      await navigator.clipboard.writeText(firestorePath);
      globalActions.setSnackbarMessage({
        message: t('organizations.path_copied', 'Firestore path copied to clipboard'),
        severity: 'success',
      });
    } catch (err) {
      globalActions.setSnackbarMessage({
        message: t('organizations.copy_failed', 'Failed to copy path'),
        severity: 'error',
      });
    }
  };

  if (isLoading || currentUser?.uid === undefined) {
    return <CircularProgress />;
  }

  return (
    <Box
      sx={{
        p: 3,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 3,
      }}
    >
      {/* Firestore Path Section for Super Admins */}
      {globalState.wsRoles.includes('admin') && (
        <Paper
          sx={{
            p: 2,
            width: '100%',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              {t('organizations.firestore_path', 'Firestore Path')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                backgroundColor: 'grey.100',
                px: 1,
                py: 0.5,
                borderRadius: 0.5,
                flex: 1,
                wordBreak: 'break-all',
              }}
            >
              /databases/(default)/documents/organizations/
              {organization.id}
            </Typography>
            <Tooltip title={t('organizations.copy_path', 'Copy path to clipboard')}>
              <IconButton
                size="small"
                onClick={handleCopyPath}
                sx={{ color: 'primary.main' }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      )}

      <Members
        organizationId={organization.id}
        members={members}
        setMembers={setMembers}
        pendingMembers={pendingMembers}
        setpendingMembers={setPendingMembers}
      />
      <Typography component="span" variant="h4">Applications</Typography>
      {members.length > 0 && members.some((m) => m.email) ? (
        <OrgApps organizationId={organization.id} members={members} />
      ) : (
        <CircularProgress />
      )}
      <Typography component="span" variant="h4">{t('organizations.membership')}</Typography>
      <MyMembership organizationId={organization.id} member={members.find((m) => m.id === currentUser.uid) || null} />
    </Box>
  );
}
